import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  enterpriseApi,
  type GDPRListParams,
  type CreateGDPRRequestInput,
  type EInvoiceListParams,
  type BackupJobType,
  type UpdateBackupScheduleInput,
  type CreateApiKeyInput,
  type CreateWebhookInput,
  type UpdateFeatureFlagInput,
  type CreateExportInput,
} from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  gdpr: (params: GDPRListParams) => ["enterprise-gdpr", params] as const,
  einvoice: (params: EInvoiceListParams) => ["enterprise-einvoice", params] as const,
  backup: ["enterprise-backup"] as const,
  apiKeys: ["enterprise-api-keys"] as const,
  webhooks: ["enterprise-webhooks"] as const,
  webhookDeliveries: (id: string) => ["enterprise-webhook-deliveries", id] as const,
  health: ["enterprise-health"] as const,
  performance: ["enterprise-performance"] as const,
  flags: ["enterprise-feature-flags"] as const,
  dataExport: ["enterprise-data-exports"] as const,
} as const;

// ---------------------------------------------------------------------------
// GDPR
// ---------------------------------------------------------------------------

export function useGDPRRequests(params: GDPRListParams = {}) {
  return useQuery({
    queryKey: KEYS.gdpr(params),
    queryFn: () => enterpriseApi.gdpr.list(params),
    staleTime: 30_000,
  });
}

export function useCreateGDPRRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGDPRRequestInput) => enterpriseApi.gdpr.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprise-gdpr"] });
    },
  });
}

export function useProcessGDPRRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enterpriseApi.gdpr.process(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprise-gdpr"] });
    },
  });
}

export function useCompleteGDPRRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enterpriseApi.gdpr.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprise-gdpr"] });
    },
  });
}

export function useRejectGDPRRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enterpriseApi.gdpr.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprise-gdpr"] });
    },
  });
}

// ---------------------------------------------------------------------------
// E-Invoice
// ---------------------------------------------------------------------------

export function useEInvoices(params: EInvoiceListParams = {}) {
  return useQuery({
    queryKey: KEYS.einvoice(params),
    queryFn: () => enterpriseApi.einvoice.list(params),
    staleTime: 30_000,
  });
}

export function useSubmitEInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enterpriseApi.einvoice.submit(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprise-einvoice"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Backup
// ---------------------------------------------------------------------------

export function useBackup() {
  return useQuery({
    queryKey: KEYS.backup,
    queryFn: () => enterpriseApi.backup.list(),
    staleTime: 30_000,
  });
}

export function useTriggerBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (type: BackupJobType) => enterpriseApi.backup.trigger(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.backup });
    },
  });
}

export function useUpdateBackupSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateBackupScheduleInput) =>
      enterpriseApi.backup.updateSchedule(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.backup });
    },
  });
}

// ---------------------------------------------------------------------------
// Enterprise API Keys
// ---------------------------------------------------------------------------

export function useEnterpriseApiKeys() {
  return useQuery({
    queryKey: KEYS.apiKeys,
    queryFn: () => enterpriseApi.apiKeys.list(),
    staleTime: 60_000,
  });
}

export function useCreateEnterpriseApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApiKeyInput) => enterpriseApi.apiKeys.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.apiKeys });
    },
  });
}

export function useRevokeEnterpriseApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enterpriseApi.apiKeys.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.apiKeys });
    },
  });
}

// ---------------------------------------------------------------------------
// Enterprise Webhooks
// ---------------------------------------------------------------------------

export function useEnterpriseWebhooks() {
  return useQuery({
    queryKey: KEYS.webhooks,
    queryFn: () => enterpriseApi.webhooks.list(),
    staleTime: 30_000,
  });
}

export function useCreateEnterpriseWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWebhookInput) => enterpriseApi.webhooks.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.webhooks });
    },
  });
}

export function useDeleteEnterpriseWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enterpriseApi.webhooks.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.webhooks });
    },
  });
}

export function useWebhookDeliveries(webhookId: string) {
  return useQuery({
    queryKey: KEYS.webhookDeliveries(webhookId),
    queryFn: () => enterpriseApi.webhooks.deliveries(webhookId),
    staleTime: 15_000,
    enabled: Boolean(webhookId),
  });
}

// ---------------------------------------------------------------------------
// System Health
// ---------------------------------------------------------------------------

export function useSystemHealth() {
  return useQuery({
    queryKey: KEYS.health,
    queryFn: () => enterpriseApi.health.get(),
    staleTime: 0,
    refetchInterval: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Performance
// ---------------------------------------------------------------------------

export function usePerformanceMetrics() {
  return useQuery({
    queryKey: KEYS.performance,
    queryFn: () => enterpriseApi.performance.get(),
    staleTime: 0,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Feature Flags
// ---------------------------------------------------------------------------

export function useFeatureFlags() {
  return useQuery({
    queryKey: KEYS.flags,
    queryFn: () => enterpriseApi.flags.list(),
    staleTime: 30_000,
  });
}

export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      readonly id: string;
      readonly input: UpdateFeatureFlagInput;
    }) => enterpriseApi.flags.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.flags });
    },
  });
}

// ---------------------------------------------------------------------------
// Data Export
// ---------------------------------------------------------------------------

export function useDataExports() {
  return useQuery({
    queryKey: KEYS.dataExport,
    queryFn: () => enterpriseApi.dataExport.list(),
    staleTime: 15_000,
  });
}

export function useCreateDataExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExportInput) => enterpriseApi.dataExport.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.dataExport });
    },
  });
}
